import { createRoute, useLocation } from '@tanstack/react-router';
import { Header } from 'components/Header';
import { AppStoreBadge } from 'components/icons/AppStoreBadge';
import { GooglePlayBadge } from 'components/icons/GooglePlayBadge';
import { EventCard } from 'components/ui/EventCard';
import { RouterKey } from 'consts';
import { useLanguage } from 'context/language.context';
import { format } from 'date-fns';
import { arSA, enUS } from 'date-fns/locale';
import { StringKey } from 'dictionaries/string-key.enum';
import { useInvitation } from 'hooks/useInvitation';
import { cn } from 'lib/utils';
import { useEffect, useMemo } from 'react';
import { rootRoute } from 'router';

export const InvitationPage = () => {
  const { t, direction } = useLanguage();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);

  const {
    acceptMutation: { data: acceptData, isError: isAcceptError, mutate: accept },
    rejectMutation: { data: rejectData, isError: isRejectError, mutate: reject },
  } = useInvitation();

  const isAccept = location.pathname.includes('invitation/accept');
  const token = urlParams.get('invitationToken');

  useEffect(() => {
    if (!token) return;
    if (isAccept) {
      accept(token);
    } else {
      reject(token);
    }
  }, []);

  const invitationData = useMemo(
    () => [
      {
        label: StringKey.EVENT_NAME,
        value: acceptData?.eventName || '-',
      },
      {
        label: StringKey.DATE_TIME,
        value: acceptData
          ? `${format(acceptData.startDate, 'MMM d, h:mm aaa', { locale: direction === 'rtl' ? arSA : enUS })} - ${format(acceptData.endDate, 'MMM d, h:mm aaa', { locale: direction === 'rtl' ? arSA : enUS })}`
          : '-',
      },
      {
        label: StringKey.LOCATION,
        value: acceptData?.location || '-',
      },
      {
        label: StringKey.TICKET_DETAILS,
        value: acceptData
          ? acceptData.ticket.row
            ? `${t(StringKey.TICKET_TYPE_SEATING_LABEL)} | ${t(StringKey.SECTION)} ${acceptData.ticket.sector} | ${t(StringKey.ROW)} ${acceptData.ticket.row} | ${t(StringKey.PLACE)} ${acceptData.ticket.seat}`
            : t(StringKey.TICKET_TYPE_ENTRY_LABEL)
          : '-',
      },
    ],
    [acceptData],
  );

  return (
    <>
      <Header minified />
      <main className="pt-20">
        <div
          className={cn(
            'flex flex-col-reverse max-lg:items-center gap-8 lg:gap-14 px-4 md:px-20 xl:px-52 py-8 md:py-16',
            !isAcceptError && !isRejectError && 'lg:grid grid-cols-[2fr_3fr]',
          )}
        >
          {!isAcceptError && !isRejectError && (
            <div className="flex flex-col max-lg:w-[400px] max-lg:max-w-full lg:min-w-[400px] gap-4 items-center relative p-5 rounded-2xl border border-[#333333] bg-[linear-gradient(144.42deg,#151515_0%,#101010_100%)]">
              <h2 className="xs:text-xl md:text-2xl font-semibold text-primary-white-1">
                {t(isAccept ? StringKey.TICKET_DETAILS : StringKey.EXPLORE_EVENTS)}
              </h2>
              <div className="w-full h-6" />
              <img
                className="absolute left-0 right-0 top-[56px] xs:top-[68px] md:top-[72px] w-full h-6 object-cover"
                src="/assets/form-divider.png"
                alt="form-divider"
              />
              <div className="flex flex-col w-full gap-5">
                {isAccept
                  ? invitationData.map(({ label, value }, index) => (
                      <div key={index} className="flex flex-col gap-1">
                        <p className="text-sm text-gray-600">{t(label)}</p>
                        {value.toLowerCase().includes('http') ? (
                          <a
                            href={value}
                            className="text-primary-pink-1 font-medium line-clamp-1 break-all cursor-pointer"
                            target="_blank"
                          >
                            {value}
                          </a>
                        ) : (
                          <p className="text-primary-white-1 font-medium line-clamp-1 break-all">
                            {value}
                          </p>
                        )}
                      </div>
                    ))
                  : rejectData?.map((event) => <EventCard event={event} />)}
              </div>
            </div>
          )}
          <div
            className={cn(
              'flex flex-col max-lg:items-center',
              (isAcceptError || isRejectError) && 'items-center',
            )}
          >
            <h1
              className={cn(
                'flex gap-1 text-[30px] leading-[38px] md:text-[36px] md:leading-[44px] font-semibold items-center max-lg:text-center',
                (isAcceptError || isRejectError) && 'text-center',
              )}
            >
              <p className="bg-clip-text bg-label-gradient text-transparent">
                {t(
                  isAcceptError || isRejectError
                    ? StringKey.INVITATION_ERROR_TITLE
                    : isAccept
                      ? StringKey.INVITATION_ACCEPT_TITLE
                      : StringKey.INVITATION_REJECT_TITLE,
                )}
              </p>
              {/*<span className="-mt-2">{isAccept ? '🎉' : '💫'}</span>*/}
            </h1>
            <p
              className={cn(
                'text-primary-white-1 mt-4 max-lg:text-center',
                (isAcceptError || isRejectError) && 'text-center',
              )}
            >
              {t(
                isAcceptError || isRejectError
                  ? StringKey.INVITATION_ERROR_DESC
                  : isAccept
                    ? StringKey.INVITATION_ACCEPT_DESC
                    : StringKey.INVITATION_REJECT_DESC,
              )}
            </p>
            <div
              className={cn(
                'flex max-lg:justify-center max-md:flex-wrap gap-4 mt-8',
                (isAcceptError || isRejectError) && 'justify-center',
              )}
            >
              <a href="/">
                <AppStoreBadge />
              </a>
              <a href="/">
                <GooglePlayBadge />
              </a>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export const invitationAcceptPageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: RouterKey.EVENT_INVITATION_ACCEPT,
  component: InvitationPage,
});

export const invitationRejectPageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: RouterKey.EVENT_INVITATION_REJECT,
  component: InvitationPage,
});
